
import {
  Box,
  Button,
  Card,
  CardContent,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import InfluencerHeader from "./InfluencerHeader";
import MyPostProduct from "./MyPostProduct";
import Navbar from "./Navbar";

const AffiliateAnalytics = () => {
  const influencerId = localStorage.getItem("influencerID");
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [productData, setProductData] = useState([]);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [totalClicks, setTotalClicks] = useState(0);
  const [totalLinks, setTotalLinks] = useState(0);
  const [totalOrderAmount, setTotalOrderAmount] = useState(0);
  const [totalOrderCommission, setTotalOrderCommission] = useState(0);

  const [productPage, setProductPage] = useState(1);
  const [tablePage, setTablePage] = useState(0);

  const productsPerPage = 4;
  const totalProductPages = Math.ceil(productData.length / productsPerPage);
  const currentProductCards = productData.slice(
    (productPage - 1) * productsPerPage,
    productPage * productsPerPage
  );
  const currentTableProduct = productData[tablePage];

  const topProducts = productData.filter((p) => p.clicks >= 500);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth <= 760);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const res = await axios.get(
          `https://server-side-influencer.onrender.com/influencer/get-all-links/${influencerId}`
        );
        if (res.data.success) {
          const filteredData = selectedDate
            ? res.data.data.filter((item) =>
                item.createdAt.split("T")[0] === selectedDate
              )
            : res.data.data;

          setProductData(filteredData);
          setTotalClicks(res.data.totalClicks);
          setTotalLinks(res.data.totalLinks);

          const totalCommission = filteredData.reduce(
            (sum, item) => sum + (item.orderCommission || 0),
            0
          );

          setTotalOrderAmount(res.data.totalMatchedProductPrice);
          setTotalOrderCommission(totalCommission);

          const chartData = filteredData.map((item) => ({
            date: item.createdAt.split("T")[0],
            clicks: item.clicks,
          }));

          const groupedData = chartData.reduce((acc, curr) => {
            if (!acc[curr.date]) {
              acc[curr.date] = { date: curr.date, clicks: 0 };
            }
            acc[curr.date].clicks += curr.clicks;
            return acc;
          }, {});

          setAnalyticsData(Object.values(groupedData));
        }
      } catch (error) {
        console.error("Error fetching links:", error);
      }
    };

    if (influencerId) fetchLinks();
  }, [influencerId, selectedDate]);

  return (
    <div className="flex h-screen hide-scrollbar" style={{ marginLeft: isSmallScreen ? "0px" : "72px" }}>
      <div className="fixed top-0 left-0 right-0 z-40 bg-white shadow-md">
        <InfluencerHeader page="Affiliate Analytics" />
      </div>

      <div className="text-white fixed top-0 left-0 bottom-0 z-50">
        <Navbar />
      </div>

      <div className="flex-1 p-6 mt-12" style={{ marginLeft: isSmallScreen ? "0px" : "3px" }}>
        <div className="flex justify-between items-center mb-6 mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mt-12">Sales and Commission</h2>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg w-full max-w-5xl mx-auto">
          <div className="flex flex-col gap-6">
            <div className="flex justify-between items-center">
              <div className="text-lg font-semibold text-gray-700">Order Commission</div>
              <div className="text-xl font-bold text-green-600">₹ {totalOrderCommission}</div>
            </div>
            <div className="flex justify-between items-center">
              <div className="text-lg font-semibold text-gray-700">Order Amount</div>
              <div className="text-xl font-bold text-blue-600">₹ {totalOrderAmount}</div>
            </div>
            <div className="flex justify-between items-center">
              <div className="text-lg font-semibold text-gray-700">Link Clicks</div>
              <div className="text-xl font-bold text-purple-600">{totalClicks} Clicks</div>
            </div>
          </div>
        </div>
{/* d */}
        <div className="mt-16 bg-white p-6 rounded-2xl shadow-lg w-full max-w-6xl mx-auto">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Analytics Chart</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analyticsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip formatter={(value) => [`${value} Clicks`, "Clicks"]} />
              <Line type="monotone" dataKey="clicks" name="Clicks" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <Box className="mt-10 bg-white p-6 rounded-2xl shadow-lg w-full max-w-6xl mx-auto">
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => {
              setTabValue(newValue);
              setProductPage(1);
              setTablePage(0);
            }}
            textColor="primary"
            indicatorColor="primary"
            variant="fullWidth"
          >
            <Tab label="Posts" />
            <Tab label="Collections" />
            <Tab label="Single Products" />
          </Tabs>

          {tabValue === 0 && <Box className="mt-6"><MyPostProduct /></Box>}

          {tabValue === 1 && (
            <Box className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <Card className="shadow-md">
                <CardContent>
                  <Typography variant="h6">Example Collection</Typography>
                  <Typography color="textSecondary">Total Links: {totalLinks}</Typography>
                  <Typography color="textSecondary">Clicks: {totalClicks}</Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {tabValue === 2 && (
            <>
              {productData.length === 0 ? (
                <Typography className="mt-6 text-center text-gray-500">
                  No affiliate links created yet.
                </Typography>
              ) : (
                <>
                  <Box className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mt-6">
                    {currentProductCards.map((product) => (
                      <Card
                        key={product._id}
                        className="shadow-md cursor-pointer hover:shadow-xl transition"
                        onClick={() => window.open(product.affiliateLink, "_blank")}
                      >
                        <img src={product.imageUrl} alt={product.title} className="w-full h-48 object-cover rounded-t-2xl" />
                        <CardContent>
                          <Typography variant="h6">{product.title}</Typography>
                          <Typography color="textSecondary">Clicks: {product.clicks}</Typography>
                          <Typography color="textSecondary">Created At: {product.createdAt.split("T")[0]}</Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>

                  <div className="flex justify-center gap-4 mt-6">
                    <Button variant="outlined" disabled={productPage === 1} onClick={() => setProductPage((prev) => prev - 1)}>Previous Cards</Button>
                    <Button variant="outlined" disabled={productPage === totalProductPages} onClick={() => setProductPage((prev) => prev + 1)}>Next Cards</Button>
                  </div>
                </>
              )}
            </>
          )}
        </Box>

        {tabValue === 2 && productData.length > 0 && (
          <>
            <Box className="mt-10 bg-white p-6 rounded-2xl shadow-lg w-full max-w-6xl mx-auto">
              <Typography variant="h6" className="mb-4">Top Performing Products </Typography>
              {topProducts.length > 0 ? (
                <Box className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {topProducts.map((product) => (
                    <Card
                      key={product._id}
                      className="shadow-md cursor-pointer hover:shadow-xl transition"
                      onClick={() => window.open(product.affiliateLink, "_blank")}
                    >
                      <img src={product.imageUrl} alt={product.title} className="w-full h-48 object-cover rounded-t-2xl" />
                      <CardContent>
                        <Typography variant="subtitle1" className="font-semibold">{product.title}</Typography>
                        <Typography color="textSecondary">Clicks: {product.clicks}</Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Typography>No top performing products</Typography>
              )}
            </Box>

            <Box className="mt-10 bg-white p-6 rounded-2xl shadow-lg w-full max-w-6xl mx-auto">
              <Typography variant="h6" className="mb-4">
                {currentTableProduct.title} (Total Clicks: {currentTableProduct.clicks})
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>IP Address</TableCell>
                      <TableCell>Device</TableCell>
                      <TableCell>Browser</TableCell>
                      <TableCell>Platform</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentTableProduct.clickDetails.map((click, index) => (
                      <TableRow key={index}>
                        <TableCell>{click.date}</TableCell>
                        <TableCell>{click.time}</TableCell>
                        <TableCell>{click.ipAddress}</TableCell>
                        <TableCell>{click.device}</TableCell>
                        <TableCell>{click.browser}</TableCell>
                        <TableCell>{click.platform}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <div className="flex justify-center gap-4 mt-6">
                <Button variant="outlined" disabled={tablePage === 0} onClick={() => setTablePage((prev) => prev - 1)}>Previous Table</Button>
                <Button variant="outlined" disabled={tablePage === productData.length - 1} onClick={() => setTablePage((prev) => prev + 1)}>Next Table</Button>
              </div>
            </Box>
          </>
        )}
      </div>
    </div>
  );
};

export default AffiliateAnalytics;
